import React from "react";
import { dataURItoBlob, shouldRender } from "@rjsf/core/lib/utils";
import FormInputFile from "components/FormInputFile";
import { Spacing } from "folksam-ui";
import { extractNameFromBase64File } from "components/ReviewStep/review-step-utils";
import {
  Context,
  ACTIONS_TYPES,
  MAX_ATTACHMENT_SIZE,
} from "../../../providers/FileSizeProvider/FileSizeProvider";
import unionBy from "lodash.unionby";
import differenceBy from "lodash.differenceby";
import Errors from "customization/templates/Errors";
import { withTranslation } from "react-i18next";
function addNameToDataURL(dataURL, name, type, size) {
  if (size === 0) {
    return `data:${type};name=${name};base64,`;
  }
  return dataURL.replace(";base64", `;name=${name};base64`);
}

function processFile(file) {
  const { name, size, type } = file;
  return new Promise((resolve, reject) => {
    const reader = new window.FileReader();
    reader.onerror = reject;
    reader.onload = (event) => {
      resolve({
        dataURL: addNameToDataURL(event.target.result, name, type, size),
        name,
        size,
        type,
      });
    };
    reader.readAsDataURL(file);
  });
}

function processFiles(files) {
  return Promise.all([].map.call(files, processFile));
}

function FilesInfo(props) {
  const { filesInfo } = props;
  if (filesInfo.length === 0) {
    return null;
  }
  return (
    <FormInputFile.List
      files={filesInfo}
      handleOnRemoveFile={props.onRemoveFile}
    />
  );
}

function extractFileInfo(dataURLs) {
  return dataURLs
    .filter((dataURL) => typeof dataURL !== "undefined")
    .map((dataURL) => {
      const { blob, name } = dataURItoBlob(dataURL);
      return {
        name: name,
        size: blob.size,
        type: blob.type,
      };
    });
}

/**
 * This is a modifed version of the core widget
 * See https://github.com/mozilla-services/react-jsonschema-form/blob/v1.2.0/src/components/widgets/FileWidget.js
 */
class FileWidget extends React.Component {
  constructor(props) {
    super(props);
    const { value } = props;
    const values = Array.isArray(value) ? value : [value];

    this.state = { values, filesInfo: extractFileInfo(values) };
  }
  static contextType = Context;
  shouldComponentUpdate(nextProps, nextState) {
    return shouldRender(this, nextProps, nextState);
  }

  onChange = (event) => {
    const localState = this.state;
    const { multiple, onChange } = this.props;
    const context = this.context;
    processFiles(event.target.files).then((filesInfo) => {
      // we only want to add new files
      if (multiple) {
        ////////////////////////////////////////////////
        // Multiple file upload
        ///////////////////////////////////////////////
        // get new files by name together with existing files
        const newFilesInfo = unionBy(filesInfo, localState.filesInfo, "name");
        // we only want to add the new files
        const onlyNewFiles = differenceBy(
          newFilesInfo,
          localState.filesInfo,
          "name"
        );
        // add the new files to the global store of bytes
        onlyNewFiles.forEach((file) => {
          context.dispatch({
            type: ACTIONS_TYPES.ADD_ATTACHMENT,
            payload: file.size,
          });
        });
        const state = {
          values: [
            ...localState.values,
            ...onlyNewFiles
              .filter((fileInfo) => {
                return fileInfo.dataURL !== undefined;
              })
              .map((fileInfo) => fileInfo.dataURL),
          ],
          filesInfo: newFilesInfo,
        };
        if (newFilesInfo.length > 0) {
          this.setState(state, () => {
            onChange(state.values);
          });
        }
      } else {
        ////////////////////////////////////////////////
        // Single file upload
        ///////////////////////////////////////////////

        // Remove existing files sizes from global byte counter
        localState.filesInfo.forEach((file) => {
          context.dispatch({
            type: ACTIONS_TYPES.REMOVE_ATTACHMENT,
            payload: file.size,
          });
        });
        filesInfo.forEach((file) => {
          context.dispatch({
            type: ACTIONS_TYPES.ADD_ATTACHMENT,
            payload: file.size,
          });
        });

        const state = {
          values: filesInfo.map((fileInfo) => fileInfo.dataURL),
          filesInfo: filesInfo,
        };
        if (filesInfo.length > 0) {
          this.setState(state, () => {
            onChange(state.values[0]);
          });
        }
      }
    });
  };
  onRemoveFile = (name) => () => {
    const { multiple, onChange } = this.props;
    const context = this.context;

    const values = this.state.values.filter((file) => {
      const extractedName = extractNameFromBase64File(file);
      return extractedName !== name;
    });
    const filesInfo = this.state.filesInfo.filter((file) => {
      if (file.name === name) {
        context.dispatch({
          type: ACTIONS_TYPES.REMOVE_ATTACHMENT,
          payload: file.size,
        });
      }
      return file.name !== name;
    });
    const state = { values, filesInfo };

    this.setState(state, () => {
      if (multiple) {
        onChange(state.values);
      } else {
        onChange(state.values[0]);
      }
    });
  };

  render() {
    const {
      multiple,
      id,
      readonly,
      disabled,
      autofocus,
      name,
      rawErrors,
      t,
    } = this.props;
    const { filesInfo } = this.state;
    const { attachmentsSize } = this.context.state;

    return (
      <div>
        <FormInputFile
          id={id}
          label={multiple ? "Välj bilagor" : "Välj bilaga"}
          type="file"
          disabled={readonly || disabled}
          onChange={this.onChange}
          defaultValue=""
          autoFocus={autofocus}
          multiple={multiple}
          name={name}
          ref={this.myRef}
        />
        <Spacing y={"3"}>
          <FilesInfo filesInfo={filesInfo} onRemoveFile={this.onRemoveFile} />
        </Spacing>
        {attachmentsSize > MAX_ATTACHMENT_SIZE && !rawErrors ? (
          <Errors>{t("error.filesize.label")}</Errors>
        ) : null}
      </div>
    );
  }
}

FileWidget.propTypes = {};

FileWidget.defaultProps = {
  autofocus: false,
};

export default withTranslation()(FileWidget);
